import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Phone, Mail, ChevronRight, TrendingUp, TrendingDown,
  Users, CalendarCheck, MessageCircle, DollarSign, FileText, Bell, Clock, CheckCircle2,
  AlertCircle, User, Sparkles, Zap, ShieldCheck, MapPin,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";
const glass = "rgba(20,28,44,0.55)";

const METRICS = [
  { label: "New Leads Today",     value: "27",     delta: "+18%",       icon: Users,         trend: "up" },
  { label: "Appointments Booked", value: "14",     delta: "+22%",       icon: CalendarCheck, trend: "up" },
  { label: "Active Conversations",value: "9",      delta: "3 hot",      icon: MessageCircle, trend: "flat" },
  { label: "Payments Collected",  value: "$8,475", delta: "+$2,140",    icon: DollarSign,    trend: "up" },
  { label: "Quotes Pending",      value: "6",      delta: "2 overdue",  icon: FileText,      trend: "warn" },
  { label: "Follow-Ups Due",      value: "11",     delta: "4 today",    icon: Bell,          trend: "warn" },
  { label: "Conversion Rate",     value: "31.4%",  delta: "+3.2pp",     icon: TrendingUp,    trend: "up" },
  { label: "Avg Response Time",   value: "18s",    delta: "31% faster", icon: Clock,         trend: "up" },
];

const LEADS = [
  { name: "Sarah Mitchell",  biz: "Mitchell Family Law",        service: "AI Intake System",      source: "Google Search",  intent: "Hot",     status: "Consultation Booked",   assigned: "Kit",       ago: "32 seconds ago" },
  { name: "Robert Gaines",   biz: "Gaines Roofing & Restoration", service: "Website Redesign",     source: "Website Chat",   intent: "Medium",  status: "Design Form Sent",       assigned: "Kim",       ago: "1 minute ago" },
  { name: "Amanda Brooks",   biz: "Luxe Hair Studio",           service: "Booking Bot",           source: "Facebook Campaign", intent: "Hot",  status: "$299.99 Payment Link Sent", assigned: "Christine", ago: "2 minutes ago" },
  { name: "David Coleman",   biz: "Coleman Auto Group",         service: "Sales Agent",           source: "Referral",       intent: "High",    status: "Demo Scheduled",          assigned: "Tim",       ago: "3 minutes ago" },
  { name: "Marcus Reed",     biz: "Reed Commercial Striping",   service: "AI Estimator",          source: "Organic Search", intent: "Medium",  status: "Photos Requested",        assigned: "Patrick",   ago: "4 minutes ago" },
];

const APPTS = [
  { time: "8:30 AM",  biz: "Mitchell Family Law",         desc: "AI Intake Consultation",       who: "Kit",       status: "Confirmed" },
  { time: "9:15 AM",  biz: "Luxe Hair Studio",            desc: "Booking Automation Review",    who: "Christine", status: "Payment Received" },
  { time: "10:00 AM", biz: "Gaines Roofing & Restoration",desc: "Website Design Consultation",  who: "Kim",       status: "Confirmed" },
  { time: "11:30 AM", biz: "Coleman Auto Group",          desc: "AI Sales Agent Demonstration", who: "Tim",       status: "Confirmed" },
  { time: "1:00 PM",  biz: "Reed Commercial Striping",    desc: "Estimator Workflow Review",    who: "Patrick",   status: "Awaiting Photos" },
  { time: "2:30 PM",  biz: "Dallas Injury Attorneys",     desc: "After-Hours Intake Review",    who: "Kit",       status: "Confirmed" },
  { time: "4:00 PM",  biz: "Prestige Auto Repair",        desc: "AI Phone Agent Demo",          who: "Christine", status: "Rescheduled" },
];

const TEAM = [
  { name: "Kit",       role: "Founder & Sales Strategy",   status: "In consultation",   metrics: [{l:"Leads Assigned",v:"8"},{l:"Appointments Today",v:"4"},{l:"Deals Moving",v:"6"},{l:"Revenue Influenced",v:"$12,400"}] },
  { name: "Christine", role: "General Manager",            status: "Available",         metrics: [{l:"Leads Assigned",v:"6"},{l:"Appointments Today",v:"3"},{l:"Follow-Ups Due",v:"4"},{l:"Payments Confirmed",v:"3"}] },
  { name: "Tim",       role: "Google & SEO Expert",        status: "Reviewing campaign",metrics: [{l:"Leads Assigned",v:"5"},{l:"Appointments Today",v:"2"},{l:"SEO Reviews",v:"4"},{l:"Opportunities Open",v:"3"}] },
  { name: "Kim",       role: "Senior Web Developer",       status: "Preparing design",  metrics: [{l:"Projects",v:"7"},{l:"Design Reviews Today",v:"3"},{l:"Builds in Progress",v:"5"},{l:"Approvals Pending",v:"2"}] },
];

const PIPELINE = [
  { col: "New Lead",           items: ["Sarah Mitchell","Robert Gaines","Prestige Auto Repair"] },
  { col: "Contacted",          items: ["Luxe Hair Studio","Reed Commercial Striping","Greenway Roofing"] },
  { col: "Qualified",          items: ["Coleman Auto Group","Dallas Injury Attorneys","Apex Dental Group"] },
  { col: "Design Review",      items: ["Torres Construction","Royal Frenchies","Metro Garage Doors"] },
  { col: "Payment Sent",       items: ["Luxe Hair Studio","Prime Roofing","Elite Collision Center"] },
  { col: "Project Locked",     items: ["Mitchell Family Law","Coleman Auto Group","Texas Property Services"] },
  { col: "Build in Progress",  items: ["Lone Star Striping Pros","I Heart Frenchies","B2 Tailor"] },
];

const AGENT_ACTIVITY = [
  { t: "just now",   biz: "Luxe Hair Studio",         action: "AI Sales Agent detected strong buying intent. $299.99 secure payment link sent.", who: "Christine notified" },
  { t: "1m ago",     biz: "Mitchell Family Law",      action: "Consultation booked for tomorrow 8:30 AM.",                                    who: "Kit assigned" },
  { t: "3m ago",     biz: "Dallas Injury Attorneys",  action: "Intake form completed — 6 fields captured.",                                   who: "Kit notified" },
  { t: "5m ago",     biz: "Reed Commercial Striping", action: "Commercial striping opportunity — awaiting parking-lot photos.",               who: "Patrick notified" },
  { t: "8m ago",     biz: "—",                        action: "Follow-up message sent to a lead inactive for 48 hours.",                      who: "AI · autonomous" },
  { t: "11m ago",    biz: "Prestige Auto Repair",     action: "Customer requested human assistance. Call transferred.",                       who: "→ Christine" },
  { t: "14m ago",    biz: "Gaines Roofing",           action: "New website-design brief created.",                                            who: "→ Kit" },
  { t: "17m ago",    biz: "Coleman Auto Group",       action: "Payment confirmed. $1,200 deposit received.",                                  who: "Tim notified" },
  { t: "21m ago",    biz: "Coleman Auto Group",       action: "Lead moved Qualified → Project Locked.",                                       who: "Pipeline auto-update" },
];

const CONVO = [
  { role: "user", text: "We already have a website, but it does not bring us any leads." },
  { role: "ai",   text: "That may mean you don't need to start over. Your website may only need a facelift, stronger messaging, better mobile design, and a clearer way for customers to contact you." },
  { role: "user", text: "How much would that cost?" },
  { role: "ai",   text: "The cost depends on the amount of work needed, but many projects can be started with a $299.99 lock-in payment. I can collect the website details and send them directly to Kit for review." },
  { role: "user", text: "Yes, send it." },
];

const CONVO_BADGES = ["Buying Signal Detected", "Design Intake Started", "Assigned to Kit", "Payment Link Ready", "Follow-Up Scheduled"];

const TASKS = [
  { p: "Urgent",    t: "Call Sarah Mitchell before 10:00 AM" },
  { p: "Today",     t: "Review uploaded parking-lot photos" },
  { p: "Today",     t: "Approve returning-client rate" },
  { p: "Today",     t: "Follow up with Gaines Roofing" },
  { p: "Waiting",   t: "Confirm Friday design presentation" },
  { p: "Waiting",   t: "Review attorney intake disclaimer" },
  { p: "Today",     t: "Send updated proposal" },
  { p: "Today",     t: "Confirm Stripe payment" },
  { p: "Today",     t: "Assign new SEO review to Tim" },
  { p: "Completed", t: "Move completed project into onboarding" },
];

const PAYMENTS = [
  { biz: "Luxe Hair Studio",         amt: "$299.99",    status: "Paid",              who: "Christine" },
  { biz: "Coleman Auto Group",       amt: "$1,200",  status: "Deposit Paid",      who: "Tim" },
  { biz: "Mitchell Family Law",      amt: "$299.99",    status: "Pending",           who: "Kit" },
  { biz: "Prime Roofing",            amt: "$750",    status: "Paid",              who: "Kim" },
  { biz: "Reed Commercial Striping", amt: "—",       status: "Payment Link Viewed", who: "Patrick" },
  { biz: "Texas Property Services",  amt: "$500",    status: "Paid",              who: "Kit" },
];

const FILES = [
  { name: "Design questionnaire",       s: "Received" },
  { name: "Logo uploaded",              s: "Received" },
  { name: "Property photos",            s: "Received" },
  { name: "Legal-intake form signed",   s: "Approved" },
  { name: "Website credentials",        s: "Requested" },
  { name: "Service agreement",          s: "Under Review" },
  { name: "Project brief",              s: "Approved" },
  { name: "Payment receipt",            s: "Approved" },
];

const INTENT_COLOR = { Hot: "#EF4444", High: "#F97316", Medium: teal, Nurture: "#A78BFA", "Returning Customer": gold };
const STATUS_COLOR = { Confirmed: teal, "Payment Received": gold, "Awaiting Photos": "#F97316", Rescheduled: "#A78BFA", Paid: teal, "Deposit Paid": gold, Pending: "#F97316", "Payment Link Viewed": "#A78BFA" };
const PRIO_COLOR = { Urgent: "#EF4444", Today: gold, Waiting: "#A78BFA", Completed: teal };
const FILE_COLOR = { Received: teal, Requested: "#F97316", "Under Review": "#A78BFA", Approved: gold, "Needs Changes": "#EF4444" };

export default function CommandCenter() {
  useEffect(() => {
    document.title = "The Command Center — Live Platform Demo | PlugitAI";
    const setMeta = (n, c) => {
      let el = document.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement("meta"); el.name = n; document.head.appendChild(el); }
      el.content = c;
    };
    setMeta("description", "See the PlugitAI command center — a live-looking demo dashboard of leads, appointments, conversations, payments, tasks, and AI activity behind every website we build.");
    setMeta("robots", "index,follow");
  }, []);

  return (
    <div className="min-h-screen w-full" style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid="command-center-page">
      <TopNav />

      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{
             background: `radial-gradient(1200px 700px at 15% -10%, ${C.blueBright}22, transparent 60%),
                          radial-gradient(900px 600px at 90% 15%, ${teal}18, transparent 60%),
                          radial-gradient(1400px 900px at 60% 110%, #F9731615, transparent 70%)`,
           }} />

      {/* Hero */}
      <section className="relative z-10 pt-28 md:pt-40 pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>Command Center</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-6 animate-pulse"
               style={{ background: `${teal}22`, color: teal, border: `1px solid ${teal}66`, boxShadow: `0 0 24px ${teal}55` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: teal, boxShadow: `0 0 10px ${teal}` }} />
            Live platform demo — sample data
          </div>

          <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.4rem, 6vw, 6rem)", color: "white" }}>
            THIS IS WHERE IT{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>ALL GOES DOWN.</span>
          </h1>
          <p className="mt-6 max-w-[760px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
            Your customers see the website. <span className="text-white font-semibold">You</span> see
            every lead, appointment, conversation, payment, task, and sales opportunity happening behind it.{" "}
            <span className="text-white font-semibold">One login. Every business. Complete control.</span>
          </p>
        </div>
      </section>

      {/* Credibility */}
      <section className="relative z-10 py-20 md:py-24 border-y" style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}>
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: gold }}>Powering serious business</div>
          <div className="font-black leading-none"
               style={{ fontSize: "clamp(3rem, 9vw, 8rem)",
                        backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 60%, #EF4444 100%)`,
                        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                        filter: `drop-shadow(0 0 32px ${gold}55)` }}>
            $453 MILLION
          </div>
          <p className="mt-6 text-lg md:text-xl max-w-[860px] mx-auto" style={{ color: C.textDim }}>
            in combined business activity connected to the websites, sales systems, teams, and
            business operations behind this platform. Our goal:{" "}
            <span className="text-white font-semibold">support businesses approaching $1 billion in combined activity by 2028.</span>
          </p>
          <p className="mt-6 text-xs italic max-w-[720px] mx-auto" style={{ color: C.textFade }}>
            Combined business activity represents activity associated with businesses, teams, systems, and projects
            connected to our experience. It should not be interpreted as guaranteed revenue generated directly by PlugitAI.
          </p>
        </div>
      </section>

      {/* Metrics */}
      <section className="relative z-10 py-14">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <SectionLabel accent={teal} sub="LIVE PLATFORM DEMO — SAMPLE DATA">Today at a glance</SectionLabel>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {METRICS.map((m) => <MetricCard key={m.label} m={m} />)}
          </div>
        </div>
      </section>

      {/* Leads + Appts */}
      <section className="relative z-10 py-8">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-6">
          <Panel title="Leads Coming In Now" pill="LIVE" accent={teal}>
            <div className="space-y-3">
              {LEADS.map((l, i) => <LeadRow key={i} l={l} />)}
            </div>
          </Panel>
          <Panel title="Today's Appointments" pill="TODAY" accent={gold}>
            <div className="space-y-2">
              {APPTS.map((a, i) => <ApptRow key={i} a={a} />)}
            </div>
          </Panel>
        </div>
      </section>

      {/* Team */}
      <section className="relative z-10 py-8">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <SectionLabel accent={teal}>Sales team</SectionLabel>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TEAM.map((t) => <TeamCard key={t.name} t={t} />)}
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section className="relative z-10 py-8">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <SectionLabel accent={teal}>Lead pipeline</SectionLabel>
          <div className="mt-4 overflow-x-auto pb-4">
            <div className="flex gap-3 md:gap-4 min-w-max">
              {PIPELINE.map((col) => (
                <div key={col.col} className="w-[220px] shrink-0 rounded-2xl p-3 border"
                     style={{ borderColor: C.line, background: glass, backdropFilter: "blur(10px)" }}
                     data-testid={`pipeline-${col.col.toLowerCase().replace(/\s+/g,"-")}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center justify-between" style={{ color: teal }}>
                    <span>{col.col}</span>
                    <span className="px-1.5 rounded-full text-white text-[9px]" style={{ background: `${teal}44` }}>{col.items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.items.map((it, i) => (
                      <div key={i} className="p-3 rounded-xl border text-sm font-semibold text-white/90 cursor-grab"
                           style={{ borderColor: `${teal}33`, background: "rgba(10,15,30,0.5)" }}>{it}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Agent activity + Chat */}
      <section className="relative z-10 py-8">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="AI Agent Activity" pill="AUTONOMOUS" accent="#F97316">
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
              {AGENT_ACTIVITY.map((a, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-xl border" style={{ borderColor: C.line, background: "rgba(10,15,30,0.35)" }}>
                  <Sparkles size={14} className="mt-0.5 shrink-0" style={{ color: "#F97316" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/90 leading-snug">{a.action}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.16em]" style={{ color: C.textFade }}>
                      <span>{a.t}</span><span>·</span><span style={{ color: teal }}>{a.biz}</span><span>·</span><span style={{ color: gold }}>{a.who}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Active Sales Conversation" pill="LIVE" accent={teal}>
            <div className="space-y-3">
              {CONVO.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed"
                       style={m.role === "user"
                         ? { background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`, color: "white", borderBottomRightRadius: 4 }
                         : { background: "rgba(255,255,255,0.06)", color: C.text, border: `1px solid ${C.line}`, borderBottomLeftRadius: 4 }}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t flex flex-wrap gap-1.5" style={{ borderColor: C.line }}>
              {CONVO_BADGES.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.16em]"
                      style={{ background: `${teal}18`, color: teal, border: `1px solid ${teal}55` }}>
                  <CheckCircle2 size={9} /> {b}
                </span>
              ))}
            </div>
          </Panel>
        </div>
      </section>

      {/* Tasks + Payments + Files */}
      <section className="relative z-10 py-8">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Panel title="What Needs Attention" pill={`${TASKS.filter((t)=>t.p==="Urgent"||t.p==="Today").length} DUE`} accent="#EF4444">
            <div className="space-y-2">
              {TASKS.map((t, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: "rgba(10,15,30,0.35)" }}>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.18em] shrink-0"
                        style={{ background: `${PRIO_COLOR[t.p]}22`, color: PRIO_COLOR[t.p], border: `1px solid ${PRIO_COLOR[t.p]}55` }}>
                    {t.p}
                  </span>
                  <span className="text-sm text-white/90 flex-1">{t.t}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Payments" pill="STRIPE" accent={gold}>
            <div className="space-y-2">
              {PAYMENTS.map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-2 p-2.5 rounded-lg" style={{ background: "rgba(10,15,30,0.35)" }}>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white truncate">{p.biz}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.textFade }}>{p.who}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-white text-sm">{p.amt}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: STATUS_COLOR[p.status] || C.textFade }}>{p.status}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Forms and Files" pill="INTAKE" accent="#A78BFA">
            <div className="space-y-2">
              {FILES.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-2 p-2.5 rounded-lg" style={{ background: "rgba(10,15,30,0.35)" }}>
                  <span className="text-sm text-white/90 flex items-center gap-2"><FileText size={13} style={{ color: C.textDim }} /> {f.name}</span>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.16em]"
                        style={{ background: `${FILE_COLOR[f.s]}22`, color: FILE_COLOR[f.s], border: `1px solid ${FILE_COLOR[f.s]}55` }}>
                    {f.s}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-28">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 text-center">
          <h2 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2rem, 5.4vw, 5rem)", color: "white" }}>
            YOUR CUSTOMERS SEE THE WEBSITE.<br />
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>YOU CONTROL EVERYTHING BEHIND IT.</span>
          </h2>
          <p className="mt-6 max-w-[820px] mx-auto text-lg leading-relaxed" style={{ color: C.textDim }}>
            PlugitAI turns your website into a working sales system and gives you the command center to manage every
            lead, appointment, conversation, payment, form, task, and follow-up. You don&apos;t need to understand coding.
            You only need to know what&apos;s happening in your business.
          </p>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-3">
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-7 h-14 rounded-full font-semibold text-base"
               style={{ background: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`, color: "#0A0F1E", boxShadow: `0 24px 60px -20px ${gold}88` }}
               data-testid="command-cta-primary">
              Build my command center <ArrowRight size={16} />
            </a>
            <button type="button"
                    onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                    className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                    style={{ borderColor: `${teal}66`, color: "white" }}>
              See what my business could automate <ArrowUpRight size={16} />
            </button>
            <a href="mailto:cgarciathanksyou@gmail.com"
               className="text-sm underline underline-offset-4"
               style={{ color: teal }}>
              Already worked with us? Email Kit directly.
            </a>
          </div>
        </div>
      </section>

      <PlugitAIChat />
    </div>
  );
}

// ──── Reusable panels ────
function SectionLabel({ accent, sub, children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3">
      <h2 className="font-black" style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)", color: "white" }}>{children}</h2>
      {sub && (
        <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded-full"
              style={{ color: accent, background: `${accent}18`, border: `1px solid ${accent}55` }}>{sub}</span>
      )}
    </div>
  );
}

function Panel({ title, pill, accent, children }) {
  return (
    <div className="rounded-2xl p-5 md:p-6 border"
         style={{ borderColor: C.line, background: glass, backdropFilter: "blur(12px)" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black text-white text-base md:text-lg">{title}</h3>
        {pill && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.18em]"
                style={{ color: accent, background: `${accent}18`, border: `1px solid ${accent}55` }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
            {pill}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function MetricCard({ m }) {
  const Icon = m.icon;
  const dColor = m.trend === "up" ? teal : m.trend === "warn" ? "#F97316" : C.textDim;
  return (
    <div className="rounded-2xl p-4 md:p-5 border relative overflow-hidden"
         style={{ borderColor: C.line, background: glass, backdropFilter: "blur(12px)" }}>
      <div className="flex items-center justify-between mb-3">
        <Icon size={16} style={{ color: teal }} />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-0.5" style={{ color: dColor }}>
          {m.trend === "up" ? <TrendingUp size={10} /> : m.trend === "warn" ? <AlertCircle size={10} /> : null}
          {m.delta}
        </span>
      </div>
      <div className="text-2xl md:text-3xl font-black text-white leading-none">{m.value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.18em]" style={{ color: C.textFade }}>{m.label}</div>
    </div>
  );
}

function LeadRow({ l }) {
  const ic = INTENT_COLOR[l.intent] || teal;
  return (
    <div className="p-3 md:p-4 rounded-xl border grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto] gap-3 items-center"
         style={{ borderColor: C.line, background: "rgba(10,15,30,0.4)" }}>
      <div className="min-w-0">
        <div className="font-bold text-white text-sm truncate">{l.name} · <span className="font-normal text-white/70">{l.biz}</span></div>
        <div className="text-[10px] uppercase tracking-[0.16em] mt-0.5" style={{ color: C.textFade }}>
          {l.service} · {l.source} · assigned {l.assigned}
        </div>
      </div>
      <div className="text-xs text-white/80">{l.status}</div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.16em]"
              style={{ background: `${ic}22`, color: ic, border: `1px solid ${ic}55` }}>{l.intent}</span>
        <span className="text-[10px]" style={{ color: C.textFade }}>{l.ago}</span>
      </div>
    </div>
  );
}

function ApptRow({ a }) {
  const sc = STATUS_COLOR[a.status] || teal;
  return (
    <div className="p-3 rounded-xl border flex items-center gap-3"
         style={{ borderColor: C.line, background: "rgba(10,15,30,0.4)" }}>
      <div className="text-xs font-black text-white tabular-nums shrink-0 w-16">{a.time}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-white truncate">{a.biz}</div>
        <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.textFade }}>{a.desc} · {a.who}</div>
      </div>
      <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.16em] shrink-0"
            style={{ background: `${sc}22`, color: sc, border: `1px solid ${sc}55` }}>{a.status}</span>
    </div>
  );
}

function TeamCard({ t }) {
  const online = t.status === "Available";
  const busy = !online;
  return (
    <div className="rounded-2xl p-5 border" style={{ borderColor: C.line, background: glass, backdropFilter: "blur(12px)" }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center font-black text-white relative"
             style={{ background: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)` }}>
          {t.name[0]}
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2"
                style={{ background: online ? teal : "#F97316", borderColor: C.ink, boxShadow: `0 0 8px ${online ? teal : "#F97316"}` }} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-white">{t.name}</div>
          <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.textFade }}>{t.role}</div>
        </div>
      </div>
      <div className="text-xs mb-3 flex items-center gap-1.5" style={{ color: busy ? "#F97316" : teal }}>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: busy ? "#F97316" : teal }} />
        {t.status}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {t.metrics.map((m) => (
          <div key={m.l}>
            <div className="text-lg font-black text-white leading-none">{m.v}</div>
            <div className="text-[9px] uppercase tracking-[0.16em] mt-1" style={{ color: C.textFade }}>{m.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
